import React from 'react';
import { useTranslation } from 'react-i18next';
import { datadogRum } from '@datadog/browser-rum';
import { Box, Divider } from '@mui/material';
import { ReactComponent as StoreIcon } from 'images/icons/store.svg';
import { ReactComponent as MovingBoxIcon } from 'images/icons/moving-box.svg';
import { toCurrency } from 'utilities/currency';
import { isMobileBrowser } from 'utilities/user_agent';
import { PickupOption } from 'typescript/types/api/pickup_options';
import {
  durationStr,
  fullLocationName,
  readyDateStr,
} from 'typescript/utilities/bopis_strings';

export interface ShippingQuote {
  min?: number;
  max: number;
  verb: string;
  time_unit: string;
  expedited?: ShippingQuote;
}

interface FulfillmentSectionProps {
  quote: ShippingQuote;
  expeditedAvailable: boolean;
  bopisEligible: boolean;
  expeditedCost?: number;
  bopisPickupOption?: PickupOption;
  onBopisLocationClick?: () => void;
}

const FulfillmentSection = ({
  quote,
  expeditedAvailable,
  bopisEligible,
  expeditedCost,
  bopisPickupOption,
  onBopisLocationClick,
}: FulfillmentSectionProps) => {
  if (!quote) return null;

  const { expedited, verb } = quote;
  const { t, i18n } = useTranslation();
  const isMobile = isMobileBrowser();

  const SHIP_VERB = 'ship';

  const getEstimateText = ({ min, max, time_unit }: ShippingQuote): string => {
    // if min is nil or 0, show max
    if (!min || min === 0) {
      return t('productPage.shippingEstimateSingle', {
        max,
        timeUnit: t(`shipping.shippingEstimate.timeUnits.${time_unit}.one`),
      });
    }

    // Otherwise, show a range
    return t('productPage.shippingEstimate', {
      min,
      max,
      timeUnit: t(`shipping.shippingEstimate.timeUnits.${time_unit}.other`),
    });
  };

  const getBOPISReadyText = (pickupOption: PickupOption) => {
    const {
      availability_date,
      days_until_available,
      location_id,
      open_now,
      pickup_ready_in_minutes,
      timezone,
    } = pickupOption;

    datadogRum.setGlobalContextProperty('bopis_location_id', location_id);
    datadogRum.setGlobalContextProperty('bopis_timezone', timezone);

    const readyText =
      pickup_ready_in_minutes && open_now
        ? durationStr(pickup_ready_in_minutes)
        : readyDateStr(days_until_available, availability_date, timezone);

    return `${readyText} ${t('productPage.pickup.at')}`;
  };

  // Get just the timing text without the prefixes
  const standardShippingTime = getEstimateText(quote);
  const expeditedShippingTime =
    expeditedAvailable && expedited ? getEstimateText(expedited) : '';

  const expeditedCostText = expeditedCost
    ? toCurrency(expeditedCost)
    : t('productPage.calculatedAtCheckout');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <Divider />

      {/* Content Container */}
      <Box
        sx={{
          display: 'flex',
          gap: '24px',
          flexWrap: 'nowrap',
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        {/* In-store pickup section */}
        {bopisEligible && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              gap: '8px',
            }}
          >
            <Box
              sx={{
                width: '24px',
                height: '24px',
                position: 'relative',
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              <StoreIcon
                style={{
                  position: 'absolute',
                  left: '2px',
                  top: '4px',
                }}
              />
            </Box>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              {bopisPickupOption ? (
                <>
                  <Box
                    sx={{
                      fontSize: '14px',
                      lineHeight: '22px',
                      fontFamily: 'Square Sans Text',
                      fontWeight: 400,
                    }}
                  >
                    {getBOPISReadyText(bopisPickupOption)}
                  </Box>
                  <Box
                    sx={{
                      fontSize: '14px',
                      lineHeight: '22px',
                      fontFamily: 'Square Sans Text',
                      fontWeight: 500,
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.8,
                      },
                    }}
                    onClick={onBopisLocationClick}
                  >
                    {fullLocationName(bopisPickupOption)}
                  </Box>
                </>
              ) : (
                <>
                  <Box
                    sx={{
                      fontSize: '14px',
                      fontFamily: 'Square Sans Text',
                      fontWeight: 400,
                      lineHeight: '22px',
                    }}
                  >
                    {t('cart.label.inStorePickupAvailable')}
                  </Box>
                  <Box
                    sx={{
                      fontSize: '14px',
                      lineHeight: '22px',
                      fontFamily: 'Square Sans Text',
                    }}
                  >
                    <span style={{ fontWeight: 500 }}>
                      {i18n.format(
                        t('checkout.delivery.pickup.today'),
                        'capitalize',
                      )}{' '}
                    </span>
                    <span style={{ fontWeight: 400 }}>
                      {t('productPage.atSelectBestBuys')}
                    </span>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        )}

        {/* Delivery section */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            gap: '8px',
          }}
        >
          <Box
            sx={{
              width: '24px',
              height: '24px',
              position: 'relative',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            <MovingBoxIcon
              style={{
                position: 'absolute',
                left: '5px',
                top: '1.88px',
              }}
            />
          </Box>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                fontSize: '14px',
                fontFamily: 'Square Sans Text',
                fontWeight: 400,
                lineHeight: '22px',
              }}
            >
              {verb === SHIP_VERB
                ? t('productPage.orderTodayShipsToYou')
                : t('productPage.orderTodayDeliversToYou')}
            </Box>
            {expeditedAvailable && (
              <Box
                sx={{
                  fontSize: '14px',
                  fontFamily: 'Square Sans Text',
                  fontWeight: 500,
                  lineHeight: '22px',
                }}
              >
                {t('productPage.shippingTimeAndCost', {
                  shippingTime: expeditedShippingTime,
                  cost: expeditedCostText,
                })}
              </Box>
            )}
            <Box
              sx={{
                fontSize: '14px',
                fontFamily: 'Square Sans Text',
                fontWeight: 500,
                lineHeight: '22px',
              }}
            >
              {t('productPage.shippingTimeAndCost', {
                shippingTime: standardShippingTime,
                cost: t('checkout.shipping.cost.free'),
              })}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default FulfillmentSection;
